import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { doc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { toast } from 'sonner';
import { User, CreditCard, ExternalLink, Check, Wallet, AlertCircle, Loader2, CheckCircle2, Trash2 } from 'lucide-react';
import { db, cloudFunctions, logAnalyticsEvent } from '@/lib/firebase';
import { useAuth } from '@/hooks/useAuth';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import { GlassCard } from '@/components/ui/GlassCard';
import { Badge } from '@/components/ui/Badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/Tabs';
import { isLaunchWeekActive, LAUNCH_REGULAR_PRICES } from '@/components/ui/LaunchCountdown';
import { PayoutCountryPicker, PayoutCountryMismatch, suggestPayoutCountry, useConnectCountries } from '@/components/payouts/PayoutCountryPicker';
import { countryName } from '@/lib/countries';
import { PLAN_CONFIGS } from '@shared/types';


interface ConnectStatusType {
  hasAccount: boolean;
  isOnboarded: boolean;
  canReceivePayments: boolean;
  status?: string;
  requirements?: string[];
  country?: string | null;
  billingCountry?: string | null;
  canChangeCountry?: boolean;
}

export function SettingsPage() {
  const { user, userData } = useAuth();
  const [searchParams] = useSearchParams();
  const [saving, setSaving] = useState(false);
  const [upgrading, setUpgrading] = useState(false);
  const [changingPlan, setChangingPlan] = useState<string | null>(null);
  const [syncingSubscription, setSyncingSubscription] = useState(false);
  const [connectLoading, setConnectLoading] = useState(false);
  const [portalLoading, setPortalLoading] = useState(false);
  const [cancellingDowngrade, setCancellingDowngrade] = useState(false);
  const [deletingAccount, setDeletingAccount] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [connectStatus, setConnectStatus] = useState<ConnectStatusType | null>(null);
  const [loadingConnectStatus, setLoadingConnectStatus] = useState(true);
  const [payoutCountry, setPayoutCountry] = useState('');
  const { data: connectCountries, isLoading: loadingConnectCountries } = useConnectCountries();
  const [activeTab, setActiveTab] = useState('profile');
  const [profile, setProfile] = useState({
    fullName: userData?.fullName || '',
    companyName: userData?.companyName || '',
    targetLocation: userData?.targetLocation || '',
  });

  // Handle tab from URL param
  useEffect(() => {
    const tabParam = searchParams.get('tab');
    if (tabParam && ['profile', 'billing', 'payouts'].includes(tabParam)) {
      setActiveTab(tabParam);
    }
  }, [searchParams]);


  // Handle return from Stripe checkout - sync subscription status
  useEffect(() => {
    const checkoutParam = searchParams.get('checkout');
    if (checkoutParam === 'success') {
      // Sync subscription status after returning from Stripe
      setSyncingSubscription(true);
      toast.info('Syncing your subscription...');

      cloudFunctions.syncSubscriptionStatus({})
        .then((result) => {
          const plan = PLAN_CONFIGS[result.planId as keyof typeof PLAN_CONFIGS];
          // Dedupe key so GA4/Ads doesn't double-count if the user refreshes
          // with ?checkout=success still in the URL.
          const txn = `${user?.uid ?? 'anon'}_${result.planId}`;
          const purchaseParams = plan && plan.price > 0
            ? {
                plan_id: result.planId,
                currency: 'USD',
                value: plan.price,
                transaction_id: txn,
                items: [{ item_id: result.planId, item_name: plan.name, price: plan.price }],
              }
            : { plan_id: result.planId };
          if (result.synced) {
            toast.success(`Updated to the ${PLAN_CONFIGS[result.planId as keyof typeof PLAN_CONFIGS]?.name ?? result.planId} plan!`);
            logAnalyticsEvent('purchase', purchaseParams);
          } else if (result.subscriptionActive) {
            toast.success('Subscription confirmed!');
            logAnalyticsEvent('purchase', purchaseParams);
          }
        })
        .catch((error) => {
          console.error('Failed to sync subscription:', error);
          toast.error('Failed to sync subscription. Please refresh the page.');
        })
        .finally(() => {
          setSyncingSubscription(false);
        });
    }
  }, [searchParams]);

  // Handle return from Stripe Connect onboarding
  useEffect(() => {
    const connectParam = searchParams.get('connect');
    if (connectParam === 'complete') {
      logAnalyticsEvent('stripe_connect_returned');
      toast.success('Stripe Connect setup completed! Checking status...');
      refreshConnectStatus().then(async (status) => {
        // canReceivePayments is the only signal that distinguishes a real
        // completion from a user who clicked Done on Stripe but still has
        // outstanding requirements.
        if (status?.canReceivePayments) {
          logAnalyticsEvent('stripe_connect_completed');
        } else {
          logAnalyticsEvent('stripe_connect_incomplete', {
            onboarded: status?.isOnboarded ?? false,
          });
        }
        // Process any pending commissions now that Connect is set up
        try {
          const result = await cloudFunctions.processPendingCommissions({});
          if (result.processed > 0) {
            toast.success(`${result.processed} pending payout${result.processed > 1 ? 's' : ''} processed! Check your Stripe dashboard.`);
          }
        } catch {
          // Silently ignore - no pending commissions or account isn't ready yet
        }
      });
    } else if (connectParam === 'refresh') {
      logAnalyticsEvent('stripe_connect_refresh');
      toast.info('Please complete your Stripe Connect setup');
    }
  }, [searchParams]);

  // Fetch Connect status on mount
  useEffect(() => {
    refreshConnectStatus();
  }, []);

  // Preselect the payout country once the account status and Stripe's country
  // list are known. An account that isn't live yet and sits in a different
  // country from the seller's card gets the card's country preselected, so
  // continuing moves it (the server replaces the account).
  useEffect(() => {
    if (!connectStatus || !connectCountries || payoutCountry) return;
    const suggested = suggestPayoutCountry(connectCountries, connectStatus.billingCountry);
    if (connectStatus.hasAccount && connectStatus.country) {
      setPayoutCountry(connectStatus.canChangeCountry && suggested ? suggested : connectStatus.country);
    } else {
      setPayoutCountry(suggested);
    }
  }, [connectStatus, connectCountries, payoutCountry]);

  const refreshConnectStatus = async (): Promise<ConnectStatusType | null> => {
    setLoadingConnectStatus(true);
    try {
      const status = await cloudFunctions.getConnectStatus({});
      setConnectStatus(status);
      return status;
    } catch (error) {
      toast.error('Failed to load payout status');
      return null;
    } finally {
      setLoadingConnectStatus(false);
    }
  };

  const handleStartConnect = async () => {
    setConnectLoading(true);
    logAnalyticsEvent('stripe_connect_started', { source: 'settings', country: payoutCountry });
    try {
      // The server creates the account in this country, or - if the existing
      // account isn't live yet and is in another country - replaces it.
      const result = await cloudFunctions.getConnectOnboardingLink({ country: payoutCountry });
      window.location.href = result.url;
    } catch (error) {
      logAnalyticsEvent('stripe_connect_start_failed', { source: 'settings' });
      toast.error(error instanceof Error && error.message ? error.message : 'Failed to start Connect onboarding');
      setConnectLoading(false);
    }
  };

  const handleOpenConnectDashboard = async () => {
    setConnectLoading(true);
    try {
      const result = await cloudFunctions.getConnectLoginLink({});
      window.open(result.url, '_blank');
    } catch (error) {
      toast.error('Failed to open Stripe dashboard');
    } finally {
      setConnectLoading(false);
    }
  };

  const handleSaveProfile = async () => {
    if (!user) return;
    setSaving(true);
    try {
      await updateDoc(doc(db, 'users', user.uid), {
        ...profile,
        updatedAt: serverTimestamp(),
      });
      toast.success('Profile updated');
    } catch (error) {
      toast.error('Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  const handleChangePlan = async (planId: 'starter' | 'cashflow' | 'scale') => {
    // If user has an active subscription, use updateSubscription for plan changes
    if (userData?.stripeSubscriptionId && userData?.subscriptionActive) {
      setChangingPlan(planId);
      try {
        const result = await cloudFunctions.updateSubscription({ newPlanId: planId });

        if (result.type === 'upgrade') {
          toast.success(`Upgraded to ${PLAN_CONFIGS[planId].name}! Your new plan is active now.`);
          // Refresh the page to show new plan
          window.location.reload();
        } else {
          // Downgrade scheduled
          const effectiveDate = result.effectiveDate ? new Date(result.effectiveDate).toLocaleDateString() : 'next billing cycle';
          toast.success(`Downgrade to ${PLAN_CONFIGS[planId].name} scheduled for ${effectiveDate}`);
          // Refresh to show pending change
          window.location.reload();
        }
      } catch (error) {
        toast.error('Failed to change plan');
      } finally {
        setChangingPlan(null);
      }
    } else {
      // No active subscription, use checkout flow
      setUpgrading(true);
      try {
        const result = await cloudFunctions.createCheckout({
          planId,
          successUrl: `${window.location.origin}/dashboard/settings?tab=billing&checkout=success`,
          cancelUrl: `${window.location.origin}/dashboard/settings?tab=billing&checkout=cancelled`,
        });
        window.location.href = result.sessionUrl;
      } catch (error) {
        toast.error('Failed to start checkout');
        setUpgrading(false);
      }
    }
  };

  const handleCancelPendingDowngrade = async () => {
    setCancellingDowngrade(true);
    try {
      await cloudFunctions.cancelPendingPlanChange({});
      toast.success('Pending plan change cancelled');
      window.location.reload();
    } catch (error) {
      toast.error('Failed to cancel pending change');
    } finally {
      setCancellingDowngrade(false);
    }
  };

  const handleManageBilling = async () => {
    setPortalLoading(true);
    try {
      const result = await cloudFunctions.createPortalSession({});
      window.location.href = result.url;
    } catch (error) {
      toast.error('Failed to open billing portal');
      setPortalLoading(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (deleteConfirmText !== 'DELETE') return;
    setDeletingAccount(true);
    try {
      await cloudFunctions.deleteAccount({});
      toast.success('Account deleted. Goodbye!');
      // Auth state listener will handle redirect
    } catch (error) {
      toast.error('Failed to delete account. Please try again or contact support.');
      setDeletingAccount(false);
    }
  };

  const currentPlan = userData?.planId || 'free';

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-3xl font-bold text-snow uppercase tracking-widest">Settings</h1>
        <p className="text-gray-400 mt-1">Manage your account and preferences</p>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList>
          <TabsTrigger value="profile">
            <User className="w-4 h-4 mr-2" />
            Profile
          </TabsTrigger>
          <TabsTrigger value="billing">
            <CreditCard className="w-4 h-4 mr-2" />
            Billing
          </TabsTrigger>
          <TabsTrigger value="payouts">
            <Wallet className="w-4 h-4 mr-2" />
            Payouts
          </TabsTrigger>
        </TabsList>

        <TabsContent value="profile" className="mt-6">
          <GlassCard>
            <h2 className="text-lg font-semibold text-white mb-6">Profile Information</h2>

            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="fullName">Full Name</Label>
                <Input
                  id="fullName"
                  value={profile.fullName}
                  onChange={(e) => setProfile({ ...profile, fullName: e.target.value })}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="companyName">Company Name</Label>
                <Input
                  id="companyName"
                  value={profile.companyName}
                  onChange={(e) => setProfile({ ...profile, companyName: e.target.value })}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="targetLocation">Target Location</Label>
                <Input
                  id="targetLocation"
                  value={profile.targetLocation}
                  onChange={(e) => setProfile({ ...profile, targetLocation: e.target.value })}
                />
              </div>

              <div className="space-y-2">
                <Label>Email</Label>
                <Input value={user?.email || ''} disabled className="opacity-60" />
                <p className="text-xs text-gray-500">Contact support to change your email</p>
              </div>

              <Button onClick={handleSaveProfile} loading={saving}>
                Save Changes
              </Button>
            </div>
          </GlassCard>

          {/* Danger Zone */}
          <GlassCard className="mt-6 border-red-500/20">
            <div className="flex items-center gap-3 mb-4">
              <Trash2 className="w-5 h-5 text-red-400" />
              <h2 className="text-lg font-semibold text-red-400">Danger Zone</h2>
            </div>
            <p className="text-sm text-gray-400 mb-4">
              Permanently delete your account and all associated data. This will cancel your subscription,
              remove all campaigns, leads, sites, and outreach history. This action cannot be undone.
            </p>
            <div className="space-y-3">
              <div className="space-y-2">
                <Label htmlFor="deleteConfirm" className="text-red-400/80">
                  Type <span className="font-mono font-bold">DELETE</span> to confirm
                </Label>
                <Input
                  id="deleteConfirm"
                  value={deleteConfirmText}
                  onChange={(e) => setDeleteConfirmText(e.target.value)}
                  placeholder="DELETE"
                  className="max-w-xs"
                />
              </div>
              <Button
                variant="outline"
                onClick={handleDeleteAccount}
                loading={deletingAccount}
                disabled={deleteConfirmText !== 'DELETE' || deletingAccount}
                className="border-red-500/30 text-red-400 hover:bg-red-500/10"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Delete My Account
              </Button>
            </div>
          </GlassCard>
        </TabsContent>

        <TabsContent value="billing" className="mt-6 space-y-6">
          {/* Pending Downgrade Banner */}
          {userData?.pendingPlanChange && (
            <GlassCard className="border-yellow-500/30 bg-yellow-500/5">
              <div className="flex items-start justify-between">
                <div className="flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-yellow-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <h3 className="text-sm font-semibold text-yellow-400">Pending Plan Change</h3>
                    <p className="text-xs text-gray-400 mt-1">
                      Downgrading to <span className="text-white font-medium">{PLAN_CONFIGS[userData.pendingPlanChange.newPlanId as keyof typeof PLAN_CONFIGS]?.name || userData.pendingPlanChange.newPlanId}</span> on{' '}
                      <span className="text-white font-medium">
                        {userData.pendingPlanChange.effectiveDate?.toDate?.()
                          ? userData.pendingPlanChange.effectiveDate.toDate().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
                          : new Date(userData.pendingPlanChange.effectiveDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                      </span>
                    </p>
                    <p className="text-xs text-gray-500 mt-1">
                      You will keep full access to your current plan until then.
                    </p>
                  </div>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleCancelPendingDowngrade}
                  loading={cancellingDowngrade}
                  disabled={cancellingDowngrade}
                >
                  Cancel Change
                </Button>
              </div>
            </GlassCard>
          )}

          {/* Lifetime users have no subscription, so none of the four cards below
              can render as "current". Without this banner they'd see a billing
              tab that looks like they're on no plan at all. It also states the
              one thing they most need to know: the allowance does not renew. */}
          {currentPlan === 'lifetime' && (
            <GlassCard className="ring-2 ring-primary-500">
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div>
                  <div className="flex items-center gap-2">
                    <h3 className="text-sm font-semibold text-white">
                      Current plan · {PLAN_CONFIGS.lifetime.name}
                    </h3>
                    <span className="px-2 py-0.5 rounded-full bg-primary-500/20 border border-primary-500/40 text-[9px] font-bold tracking-wider uppercase text-primary-200">
                      Paid once
                    </span>
                  </div>
                  <p className="text-xs text-gray-400 mt-1.5 tabular-nums">
                    {(userData?.usage?.leadsUsed ?? 0).toLocaleString()} /{' '}
                    {PLAN_CONFIGS.lifetime.quotas.leadsLimit.toLocaleString()} leads
                    <span className="text-gray-600 mx-2">·</span>
                    {(userData?.usage?.sitesUsed ?? 0).toLocaleString()} /{' '}
                    {PLAN_CONFIGS.lifetime.quotas.sitesLimit.toLocaleString()} sites
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    This is a one-time allowance. It doesn't reset monthly. Move to a monthly plan
                    below for a renewing quota.
                  </p>
                </div>
              </div>
            </GlassCard>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {(['starter', 'cashflow', 'scale'] as const).map((planId) => {
              const plan = PLAN_CONFIGS[planId];
              const isCurrent = currentPlan === planId;
              const isPendingDowngrade = userData?.pendingPlanChange?.newPlanId === planId;
              const isChangingToThis = changingPlan === planId;

              const showLaunch = isLaunchWeekActive();
              const regularPrice = LAUNCH_REGULAR_PRICES[planId];
              return (
                <GlassCard key={planId} className={`${isCurrent ? 'ring-2 ring-primary-500' : ''} ${isPendingDowngrade ? 'ring-2 ring-yellow-500/50' : ''} relative`}>
                  {showLaunch && (
                    <span
                      className="absolute -top-2 left-4 px-2 py-0.5 rounded-full bg-primary-500 text-[9px] font-bold tracking-wider uppercase text-white"
                      style={{ boxShadow: '0 0 12px rgba(176,128,128,0.5)' }}
                    >
                      Launch Offer
                    </span>
                  )}
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <h3 className="text-lg font-semibold text-white">{plan.name}</h3>
                      {showLaunch ? (
                        <p className="mt-1 font-display flex items-baseline gap-2">
                          <span className="line-through text-gray-500 text-base font-normal">${regularPrice}</span>
                          <span className="text-2xl font-bold text-white">
                            ${plan.price}
                            <span className="text-xs text-gray-400 font-normal font-sans">/mo</span>
                          </span>
                        </p>
                      ) : (
                        <p className="text-2xl font-bold text-white mt-1 font-display">
                          ${plan.price}<span className="text-xs text-gray-400 font-normal font-sans">/mo</span>
                        </p>
                      )}
                    </div>
                    {isCurrent && (
                      <Badge variant="accent">Current</Badge>
                    )}
                    {isPendingDowngrade && !isCurrent && (
                      <Badge className="bg-yellow-500/20 text-yellow-400 border-yellow-500/30">Scheduled</Badge>
                    )}
                    {plan.popular && !isCurrent && !isPendingDowngrade && (
                      <Badge variant="secondary">Popular</Badge>
                    )}
                  </div>

                  <p className="text-xs text-gray-500 mb-4">{plan.tagline}</p>

                  <ul className="space-y-2 mb-6">
                    {plan.features.slice(0, 4).map((feature, i) => (
                      <li key={i} className="flex items-center gap-2 text-xs text-gray-400">
                        <Check className="w-3 h-3 text-primary-400 flex-shrink-0" />
                        {feature}
                      </li>
                    ))}
                  </ul>

                  {isCurrent ? (
                    <Button variant="outline" size="sm" className="w-full" onClick={handleManageBilling} disabled={portalLoading}>
                      {portalLoading ? <><Loader2 className="w-3 h-3 mr-1 animate-spin" /> Loading...</> : 'Manage'}
                    </Button>
                  ) : isPendingDowngrade ? (
                    <Button variant="outline" size="sm" className="w-full text-yellow-400 border-yellow-500/30" disabled>
                      Scheduled
                    </Button>
                  ) : (
                    <Button
                      variant={plan.popular ? 'default' : 'outline'}
                      size="sm"
                      className="w-full"
                      onClick={() => handleChangePlan(planId)}
                      loading={isChangingToThis || upgrading || syncingSubscription}
                      disabled={!!changingPlan || upgrading || syncingSubscription}
                    >
                      {syncingSubscription ? 'Syncing...' : isChangingToThis ? 'Processing...' : `Get ${plan.name}`}
                    </Button>
                  )}
                </GlassCard>
              );
            })}
          </div>

          <GlassCard>
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-semibold text-white">Billing Portal</h3>
                <p className="text-sm text-gray-400 mt-1">
                  Manage your subscription, payment methods, and invoices
                </p>
              </div>
              <Button variant="outline" onClick={handleManageBilling} disabled={portalLoading}>
                {portalLoading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Loading...
                  </>
                ) : (
                  <>
                    <ExternalLink className="w-4 h-4 mr-2" />
                    Open Portal
                  </>
                )}
              </Button>
            </div>
          </GlassCard>

          <GlassCard>
            <h3 className="font-semibold text-white mb-4">Current Usage</h3>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div>
                <div className="flex justify-between text-sm mb-2">
                  <span className="text-gray-400">Leads {currentPlan === 'lifetime' ? '(one-time)' : '(per month)'}</span>
                  <span className="text-white">
                    {userData?.usage?.leadsUsed || 0} / {userData?.quotas?.leadsLimit || 50}
                  </span>
                </div>
                <div className="h-2 rounded-full bg-navy-800">
                  <div
                    className="h-2 rounded-full bg-primary-500"
                    style={{
                      width: `${Math.min(
                        ((userData?.usage?.leadsUsed || 0) / (userData?.quotas?.leadsLimit || 50)) * 100,
                        100
                      )}%`,
                    }}
                  />
                </div>
              </div>

              <div>
                <div className="flex justify-between text-sm mb-2">
                  <span className="text-gray-400">Site generations {currentPlan === 'lifetime' ? '(one-time)' : '(per month)'}</span>
                  <span className="text-white">
                    {userData?.usage?.sitesUsed || 0} / {userData?.quotas?.sitesLimit || 10}
                  </span>
                </div>
                <div className="h-2 rounded-full bg-navy-800">
                  <div
                    className="h-2 rounded-full bg-gray-400"
                    style={{
                      width: `${Math.min(
                        ((userData?.usage?.sitesUsed || 0) / (userData?.quotas?.sitesLimit || 10)) * 100,
                        100
                      )}%`,
                    }}
                  />
                </div>
              </div>
            </div>

            <p className="text-xs text-gray-500 mt-4">
              Usage resets at the start of each billing cycle
            </p>
          </GlassCard>
        </TabsContent>

        <TabsContent value="payouts" className="mt-6 space-y-6">
          <GlassCard>
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-primary-500/20 to-accent-500/20 flex items-center justify-center flex-shrink-0">
                <Wallet className="w-6 h-6 text-primary-400" />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-white text-lg mb-1">Receive Payouts</h3>
                <p className="text-sm text-gray-400 mb-4">
                  Connect your bank account to receive payouts when clients purchase the websites you create.
                  We use Stripe Connect to securely process payments and transfers.
                </p>

                {loadingConnectStatus ? (
                  <div className="flex items-center gap-3 py-4">
                    <Loader2 className="w-5 h-5 text-primary-400 animate-spin" />
                    <span className="text-gray-400">Checking account status...</span>
                  </div>
                ) : connectStatus?.canReceivePayments ? (
                  <div className="space-y-4">
                    <div className="flex items-center gap-3 p-4 rounded-lg bg-primary-500/10 border border-primary-500/20">
                      <CheckCircle2 className="w-5 h-5 text-primary-400" />
                      <div>
                        <p className="font-medium text-primary-400">Connected and Active</p>
                        <p className="text-sm text-gray-400">
                          Your account is ready to receive payouts
                          {connectStatus.country ? ` in ${countryName(connectStatus.country)}` : ''}
                        </p>
                      </div>
                    </div>
                    <Button variant="outline" onClick={handleOpenConnectDashboard} loading={connectLoading}>
                      <ExternalLink className="w-4 h-4 mr-2" />
                      Open Stripe Dashboard
                    </Button>
                  </div>
                ) : connectStatus?.hasAccount ? (
                  <div className="space-y-4">
                    <div className="flex items-center gap-3 p-4 rounded-lg bg-yellow-500/10 border border-yellow-500/20">
                      <AlertCircle className="w-5 h-5 text-yellow-400" />
                      <div>
                        <p className="font-medium text-yellow-400">Setup Incomplete</p>
                        <p className="text-sm text-gray-400">
                          Complete your Stripe Connect setup to start receiving payouts
                        </p>
                      </div>
                    </div>
                    {connectStatus.canChangeCountry ? (
                      <div className="space-y-3 max-w-md">
                        {connectStatus.country && connectStatus.billingCountry &&
                          connectStatus.billingCountry !== connectStatus.country &&
                          payoutCountry === connectStatus.billingCountry && (
                            <PayoutCountryMismatch accountCountry={connectStatus.country} billingCountry={connectStatus.billingCountry} />
                          )}
                        <PayoutCountryPicker
                          value={payoutCountry}
                          onChange={setPayoutCountry}
                          countries={connectCountries}
                          loading={loadingConnectCountries}
                        />
                      </div>
                    ) : connectStatus.country ? (
                      <p className="text-sm text-gray-400">Payout country: {countryName(connectStatus.country)}</p>
                    ) : null}
                    <Button
                      onClick={handleStartConnect}
                      loading={connectLoading}
                      disabled={connectStatus.canChangeCountry && !payoutCountry}
                    >
                      Complete Setup
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-4 max-w-md">
                    <PayoutCountryPicker
                      value={payoutCountry}
                      onChange={setPayoutCountry}
                      countries={connectCountries}
                      loading={loadingConnectCountries}
                    />
                    <Button onClick={handleStartConnect} loading={connectLoading} disabled={!payoutCountry}>
                      <Wallet className="w-4 h-4 mr-2" />
                      Connect with Stripe
                    </Button>
                  </div>
                )}
              </div>
            </div>
          </GlassCard>

          <GlassCard>
            <h3 className="font-semibold text-white mb-4">How Payouts Work</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="text-center">
                <div className="w-10 h-10 rounded-full bg-primary-500/20 flex items-center justify-center mx-auto mb-3">
                  <span className="text-primary-400 font-bold">1</span>
                </div>
                <h4 className="font-medium text-white text-sm mb-1">Client Purchases</h4>
                <p className="text-xs text-gray-400">
                  When a client purchases a site you created, the payment is processed through Stripe.
                </p>
              </div>
              <div className="text-center">
                <div className="w-10 h-10 rounded-full bg-primary-500/20 flex items-center justify-center mx-auto mb-3">
                  <span className="text-primary-400 font-bold">2</span>
                </div>
                <h4 className="font-medium text-white text-sm mb-1">Earnings Deposited</h4>
                <p className="text-xs text-gray-400">
                  You keep 100% of each sale. Earnings are tracked in your dashboard.
                </p>
              </div>
              <div className="text-center">
                <div className="w-10 h-10 rounded-full bg-primary-500/20 flex items-center justify-center mx-auto mb-3">
                  <span className="text-primary-400 font-bold">3</span>
                </div>
                <h4 className="font-medium text-white text-sm mb-1">Automatic Payout</h4>
                <p className="text-xs text-gray-400">
                  Funds are automatically transferred to your bank account on a rolling basis.
                </p>
              </div>
            </div>
          </GlassCard>

          <GlassCard>
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-semibold text-white">Payout History</h3>
                <p className="text-sm text-gray-400 mt-1">
                  View your earnings and payout history
                </p>
              </div>
              {connectStatus?.canReceivePayments ? (
                <Button variant="outline" onClick={handleOpenConnectDashboard}>
                  <ExternalLink className="w-4 h-4 mr-2" />
                  View in Stripe
                </Button>
              ) : (
                <Button variant="outline" disabled>
                  <ExternalLink className="w-4 h-4 mr-2" />
                  Connect First
                </Button>
              )}
            </div>
          </GlassCard>
        </TabsContent>

      </Tabs>
    </div>
  );
}
